<template>
  <div v-if="items.length === 0" class="ren-decoration-cp ren-decoration-cp-nav is-empty">
    暂无导航入口
  </div>
  <div
    v-else
    class="ren-decoration-cp ren-decoration-cp-nav"
    :style="gridStyle">
    <span v-for="(item, index) in items" :key="itemKey(item, index)">
      <i :style="iconBoxStyle(item, index)">
        <img v-if="iconUrl(item)" :src="iconUrl(item)" alt="" />
        <template v-else>{{ iconText(item) }}</template>
      </i>
      <b :style="labelStyle">{{ itemLabel(item) }}</b>
    </span>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import {
  numberValue,
  previewBoundString,
  previewRecordsFor,
  previewServiceIconText,
  recordList,
  stringValue,
  styleColorList
} from '../../../previews/preview-helpers'
import { widgetMediaUrl } from '../../media'
import type { WidgetPreviewProps } from '../../types'

const props = defineProps<WidgetPreviewProps>()

const columns = computed(() => Math.max(2, Math.min(5, Math.round(numberValue(props.component.props?.columns, 4)))))

const items = computed(() => {
  const records = previewRecordsFor(props.component, props.componentData, recordList(props.component.props?.items))
  return records.slice(0, 12)
})

const iconPalette = computed(() => styleColorList(props.component.style?.iconPalette))

const gridStyle = computed(() => {
  const gap = Number(props.component.style?.gap)
  return {
    gridTemplateColumns: `repeat(${columns.value}, minmax(0, 1fr))`,
    gap: Number.isFinite(gap) && gap > 0 ? `${gap}px` : undefined
  }
})

const labelStyle = computed(() => {
  const color = stringValue(props.component.style?.textColor)
  return color ? { color } : undefined
})

const iconSize = computed(() => {
  const size = Number(props.component.style?.iconSize)
  return Number.isFinite(size) && size > 0 ? size : 34
})

function itemLabel(item: Record<string, unknown>) {
  return previewBoundString(
    props.component,
    props.componentData,
    'item.title',
    stringValue(item.label ?? item.title, '导航'),
    item
  )
}

function iconText(item: Record<string, unknown>) {
  const raw = previewBoundString(
    props.component,
    props.componentData,
    'item.icon',
    stringValue(item.icon),
    item
  )
  return previewServiceIconText(raw, itemLabel(item))
}

function iconUrl(item: Record<string, unknown>) {
  return widgetMediaUrl(item.imageUrl ?? item.iconUrl ?? item.image)
}

function iconBoxStyle(item: Record<string, unknown>, index: number) {
  const size = `${iconSize.value}px`
  const hasImage = Boolean(iconUrl(item))
  const palette = iconPalette.value
  const paletteColor = palette.length > 0 ? palette[index % palette.length] : ''
  const background = hasImage
    ? 'transparent'
    : stringValue(item.color ?? item.backgroundColor) ||
      paletteColor ||
      props.themeTokens?.primaryColor ||
      props.themeColor ||
      'var(--ren-theme-primary)'
  const color = stringValue(item.iconColor) || props.themeTokens?.buttonTextColor || '#fff'
  return { width: size, height: size, background, color, overflow: 'hidden' }
}

function itemKey(item: Record<string, unknown>, index: number) {
  return stringValue(item.id ?? item.code, `${itemLabel(item)}-${index}`)
}
</script>

<style scoped>
.ren-decoration-cp-nav {
  background: transparent;
}

.ren-decoration-cp-nav.is-empty {
  display: grid;
  min-height: 72px;
  place-items: center;
  color: var(--ren-theme-muted);
  font-size: 12px;
  background: transparent;
}

.ren-decoration-cp-nav b {
  color: var(--ren-theme-text);
}

.ren-decoration-cp-nav i img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: inherit;
}
</style>
